import "maplibre-gl/dist/maplibre-gl.css";
import maplibregl, { type GeoJSONSource } from "maplibre-gl";
import { NceApiClient } from "@nce/api-client";
import { NceMapController, type MapState } from "@nce/map-core";
import type { PublicEstate, PublicEstatePlan, PublicListing } from "@nce/types";
import { getEstateCategory, parseEstateCategory, type EstateCategory } from "@nce/types";

type EstateDrawerEstate = Pick<PublicEstate, "slug" | "name" | "shortName" | "shortDescription"> & {
  area: { name: string };
  accent: { primary: string };
  attributes?: PublicEstate["attributes"];
  estateCategory?: string;
};
type EstateDrawerListing = Pick<PublicListing, "reference" | "title" | "price" | "priceQualifier" | "priceVisible" | "currency" | "bedrooms" | "bathrooms"> & {
  estate: { slug: string };
  media: Array<Pick<PublicListing["media"][number], "url" | "alt">>;
};
type EstateDrawerPayload = { estates: EstateDrawerEstate[]; listings: EstateDrawerListing[] };
type EstateDrawerListingData = { listings: EstateDrawerListing[]; total: number };

declare global { interface Window { ncePlatform?: { restUrl: string; restNonce: string; dataMode: string; demoLabel: string; currency: string; locale: string; mapStyle: string; deferMap?: boolean; mapScriptUrl?: string; mapStyleUrl?: string }; } }
const config = window.ncePlatform;

function api(): NceApiClient | null {
  if (!config) return null;
  const fetcher: typeof fetch = (input, init = {}) => fetch(input, { ...init, credentials: "same-origin", headers: { ...init.headers, "X-WP-Nonce": config.restNonce } });
  return new NceApiClient(config.restUrl, fetcher);
}

function text<K extends keyof HTMLElementTagNameMap>(tag: K, value: string, className?: string): HTMLElementTagNameMap[K] {
  const element = document.createElement(tag);
  element.textContent = value;
  if (className) element.className = className;
  return element;
}

function money(value: number | null, currency = "ZAR"): string {
  return value === null ? "Price on application" : new Intl.NumberFormat(config?.locale?.replace("_", "-") || "en-ZA", { style: "currency", currency, maximumFractionDigits: 0 }).format(value);
}

function propertyUrl(reference: string): string { return `${window.location.origin}/property/${encodeURIComponent(reference)}/`; }
function estatePageUrl(frame: HTMLElement, slug: string, page = "map"): string { return new URL(`${encodeURIComponent(slug)}/${page}/`, frame.dataset.estateBase || `${window.location.origin}/`).toString(); }
function estateInformationUrl(frame: HTMLElement, slug: string): string { return new URL(`${encodeURIComponent(slug)}/`, frame.dataset.estateBase || `${window.location.origin}/`).toString(); }
function developmentPageUrl(frame: HTMLElement, estateSlug: string, developmentSlug: string, page = ""): string {
  const suffix = page ? `${encodeURIComponent(page)}/` : "";
  return new URL(`${encodeURIComponent(estateSlug)}/${encodeURIComponent(developmentSlug)}/${suffix}`, frame.dataset.estateBase || `${window.location.origin}/`).toString();
}
function estateInformationLabel(estate: EstateDrawerEstate): string {
  const name = (estate.shortName || estate.name).trim();
  return `${name}${/\bestate$/i.test(name) ? "" : " Estate"} Information`;
}

function estateInformationCta(frame: HTMLElement, estate: EstateDrawerEstate, scope: "master" | "estate"): HTMLAnchorElement {
  const link = text("a", estateInformationLabel(estate), "nce-button");
  link.href = scope === "master" ? estateInformationUrl(frame, estate.slug) : estatePageUrl(frame, estate.slug, "guide");
  return link;
}

const estateImageFiles: Record<string, string> = {
  simbithi: "simbithi.webp",
  seaton: "seaton.webp",
  fairview: "fairview.webp",
  zululami: "zululami.webp",
  lalela: "lalela.webp",
  "zimbali-lakes": "zimbali-lakes.webp",
  elaleni: "elaleni.webp",
  zimbali: "zimbali.webp",
  fable: "fable.webp",
  springvale: "springvale.webp",
  "palm-lakes": "palm-lakes.webp",
  seaward: "seaward.webp",
  dunkirk: "dunkirk.webp",
  brettenwood: "brettenwood.webp",
  helmsley: "helmsley.webp",
  tanglewood: "tanglewood.webp",
  "umhlali-golf": "umhlali-golf.webp",
  "port-zimbali": "port-zimbali.webp",
  hilltop: "hilltop.webp",
  shortens: "shortens.webp",
  caledon: "caledon.webp",
  "manor-estates": "manor-estates.webp",
  "ballito-gardens": "ballito-gardens.webp",
  brooklyn: "brooklyn.webp",
  wakenshaw: "wakenshaw.webp",
  "sheffield-manor": "sheffield-manor.webp",
  tahari: "tahari.webp",
  "beverley-hills": "beverley-hills.webp",
  lazuli: "lazuli.webp",
  "the-greens": "the-greens.webp",
  woodlands: "woodlands.webp",
  santorini: "santorini.webp",
  "sheffield-hills": "sheffield-hills.webp",
};
const galleryLedEstates = new Set(["tanglewood", "umhlali-golf", "port-zimbali", "hilltop", "shortens", "caledon", "manor-estates", "ballito-gardens", "brooklyn", "wakenshaw", "sheffield-manor", "tahari", "beverley-hills", "lazuli", "the-greens", "woodlands"]);
const refreshedEstateImages = new Set(["tahari", "elaleni", "fairview", "helmsley", "palm-lakes", "zululami", "springvale", "simbithi", "zimbali-lakes", "lalela", "seaton", "zimbali", "santorini"]);
const estateMarkerImages = Object.fromEntries(Object.entries(estateImageFiles).map(([slug, file]) => {
  const feedbackRefresh = ["tanglewood", "port-zimbali", "hilltop", "dunkirk", "brettenwood", "sheffield-hills"].includes(slug);
  const path = galleryLedEstates.has(slug) && !feedbackRefresh ? `estate-galleries/${slug}/01.webp` : `optimized/${file}`;
  const url = new URL(`/wp-content/themes/north-coast-estates/assets/images/${path}`, window.location.origin);
  if (feedbackRefresh) url.searchParams.set("ver", "20261027");
  else if (refreshedEstateImages.has(slug)) url.searchParams.set("ver", "20261022");
  return [slug, url.toString()];
}));

function estateImage(slug: string): string { return estateMarkerImages[slug] || new URL("/wp-content/themes/north-coast-estates/assets/images/hero-north-coast.jpg", window.location.origin).toString(); }

function listingDrawerContent(listing: PublicListing): DocumentFragment {
  const fragment = document.createDocumentFragment();
  fragment.append(text("p", `${listing.estate.name} · ${listing.reference}`, "nce-eyebrow"), text("h2", listing.title), text("p", listing.priceVisible ? money(listing.price, listing.currency) : listing.priceQualifier || "Price on application", "nce-card-price"));
  if (listing.summary) fragment.append(text("p", listing.summary));
  const facts = text("div", "", "nce-property-facts");
  for (const [value, label] of [[listing.bedrooms, "beds"], [listing.bathrooms, "baths"], [listing.garages, "garages"]] as const) if (value !== null) facts.append(text("span", `${value} ${value === 1 ? label.slice(0, -1) : label}`));
  fragment.append(facts);
  const link = text("a", "View property", "nce-button");
  link.href = propertyUrl(listing.reference);
  fragment.append(link);
  return fragment;
}

function developmentListingDrawerContent(frame: HTMLElement, listing: PublicListing): DocumentFragment {
  const fragment = document.createDocumentFragment();
  const article = text("article", "", "nce-development-home-drawer");
  const propertyHref = propertyUrl(listing.reference);
  const developmentName = listing.development?.name || "New-home collection";
  const developmentSlug = listing.development?.slug || frame.dataset.development || "";

  const gallery = text("div", "", "nce-development-home-drawer__gallery");
  const heroLink = text("a", "", "nce-development-home-drawer__hero");
  heroLink.href = propertyHref;
  if (listing.media[0]) {
    const hero = document.createElement("img");
    hero.src = listing.media[0].url;
    hero.alt = listing.media[0].alt;
    hero.loading = "lazy";
    hero.decoding = "async";
    heroLink.append(hero);
  }
  const heroCopy = text("span", "", "nce-development-home-drawer__hero-copy");
  heroCopy.append(text("small", `${listing.media.length} renders & drawings`), text("strong", listing.media[0]?.caption || "Architectural presentation"));
  heroLink.append(heroCopy);
  gallery.append(heroLink);
  if (listing.media.length > 1) {
    const strip = text("div", "", "nce-development-home-drawer__strip");
    for (const [index, item] of listing.media.slice(1, 4).entries()) {
      const link = text("a", "");
      link.href = propertyHref;
      link.setAttribute("aria-label", `Open ${listing.title} gallery at image ${index + 2}`);
      const image = document.createElement("img");
      image.src = item.url;
      image.alt = item.alt;
      image.loading = "lazy";
      link.append(image);
      strip.append(link);
    }
    gallery.append(strip);
  }
  article.append(gallery);

  const body = text("div", "", "nce-development-home-drawer__body");
  const identity = text("div", "", "nce-development-home-drawer__identity");
  identity.append(text("p", `${developmentName} · ${listing.reference}`, "nce-eyebrow"), text("span", "Limited new-home release"));
  body.append(identity, text("h2", listing.title));

  const availability = text("div", "", "nce-development-home-drawer__availability");
  availability.append(text("small", "Current availability"), text("strong", listing.priceVisible ? money(listing.price, listing.currency) : listing.priceQualifier || "Price on application"));
  body.append(availability);
  if (listing.summary) body.append(text("p", listing.summary, "nce-development-home-drawer__lead"));

  const facts = text("dl", "", "nce-development-home-drawer__facts");
  const addFact = (label: string, value: string | number | null): void => {
    if (value === null) return;
    const fact = text("div", "");
    fact.append(text("dt", label), text("dd", String(value)));
    facts.append(fact);
  };
  addFact("Bedrooms", listing.bedrooms);
  addFact("Bathrooms", listing.bathrooms);
  addFact("Under roof", listing.floorSizeM2 === null ? null : `${listing.floorSizeM2} m²`);
  addFact("Site", listing.erfSizeM2 === null ? null : `${listing.erfSizeM2} m²`);
  body.append(facts);

  const story = listing.description?.split(/\n\s*\n/).find(Boolean);
  if (story) body.append(text("p", story, "nce-development-home-drawer__story"));
  if (listing.features.length) {
    const featureSection = text("section", "", "nce-development-home-drawer__features");
    featureSection.append(text("h3", "Residence highlights"));
    const featureList = text("ul", "");
    for (const feature of listing.features.slice(0, 6)) {
      const item = text("li", "");
      item.append(text("span", "+"), text("strong", feature.label));
      if (feature.value !== true && feature.value !== false && feature.value !== null) item.append(text("small", String(feature.value)));
      featureList.append(item);
    }
    featureSection.append(featureList);
    body.append(featureSection);
  }

  const library = text("div", "", "nce-development-home-drawer__library");
  library.append(text("span", "Architectural pack included"), text("strong", `${Math.min(4, listing.media.length)} renders · ${Math.max(0, listing.media.length - 4)} drawings`));
  const galleryLink = text("a", "View architectural gallery →");
  galleryLink.href = developmentPageUrl(frame, listing.estate.slug, developmentSlug, "gallery");
  library.append(galleryLink);
  body.append(library);

  const actions = text("div", "", "nce-development-home-drawer__actions");
  const property = text("a", "Explore this residence", "nce-button");
  property.href = propertyHref;
  const enquire = text("a", "Enquire about availability", "nce-text-link");
  enquire.href = developmentPageUrl(frame, listing.estate.slug, developmentSlug, "contact");
  actions.append(property, enquire);
  body.append(actions, text("p", "Design-stage information supplied for buyer orientation. Final specification, dimensions, pricing and availability require confirmation.", "nce-development-home-drawer__note"));
  article.append(body);
  fragment.append(article);
  return fragment;
}

function poiFallbackImage(estateSlug: string, point: PublicEstatePlan["points"][number]): string {
  if (estateSlug === "lalela") {
    const file = point.kind === "development"
      ? "development-sienna-lalela.webp"
      : point.category?.toLowerCase() === "nature"
        ? "estate-lalela-lifestyle.webp"
        : point.category?.toLowerCase() === "lifestyle"
          ? "estate-lalela-community.webp"
          : "estate-lalela-official.webp";
    return new URL(`/wp-content/themes/north-coast-estates/assets/images/${file}`, window.location.origin).toString();
  }
  return estateImage(estateSlug);
}

function poiDrawerContent(point: PublicEstatePlan["points"][number], estateSlug: string): DocumentFragment {
  const fragment = document.createDocumentFragment();
  const article = text("article", "", "nce-poi-drawer");
  {
    const gallery = text("div", "", "nce-poi-drawer__gallery");
    const hero = document.createElement("img");
    hero.src = point.media[0]?.url || poiFallbackImage(estateSlug, point);
    hero.alt = point.media[0]?.alt || `${point.label} at ${estateSlug.replaceAll("-", " ")}`;
    hero.loading = "lazy";
    hero.decoding = "async";
    gallery.append(hero, text("span", point.category || "Estate place", "nce-poi-drawer__stamp"));
    if (point.media.length > 1) { const strip=text("div","","nce-poi-drawer__strip"); for(const item of point.media.slice(1,4)){const image=document.createElement("img");image.src=item.url;image.alt=item.alt;image.loading="lazy";strip.append(image);} gallery.append(strip); }
    article.append(gallery);
  }
  const body=text("div","","nce-poi-drawer__body");
  body.append(text("p", point.category || "Estate point of interest", "nce-eyebrow"),text("h2",point.label));
  if(point.shortDescription) body.append(text("p",point.shortDescription,"nce-poi-drawer__lead"));
  if(point.keyFacts.length){const facts=text("div","","nce-poi-drawer__facts");for(const fact of point.keyFacts){const item=text("span","");item.append(text("small",fact.label),text("strong",fact.value));facts.append(item);}body.append(facts);}
  if(point.description) body.append(text("p",point.description,"nce-poi-drawer__description"));
  body.append(text("p","Independent estate information prepared by North Coast Estates.","nce-map-geometry-status"));
  article.append(body); fragment.append(article); return fragment;
}

function estateDrawerContent(frame: HTMLElement, estate: EstateDrawerEstate, scope: "master" | "estate", listings: EstateDrawerListing[], total: number | null): DocumentFragment {
  const fragment = document.createDocumentFragment();
  const preview = text("article", "", "nce-map-estate-preview");
  const media = text("div", "", "nce-map-estate-preview__media");
  const image = document.createElement("img");
  image.src = estateImage(estate.slug);
  image.alt = `${estate.shortName || estate.name} estate preview`;
  image.loading = "lazy";
  image.decoding = "async";
  const heroCopy = text("div", "", "nce-map-estate-preview__hero-copy");
  heroCopy.append(text("p", estate.area.name, "nce-eyebrow"), text("h2", estate.shortName || estate.name));
  media.append(image, text("span", "Independent guide", "nce-map-estate-preview__stamp"), heroCopy);

  const body = text("div", "", "nce-map-estate-preview__body");
  body.append(text("p", scope === "master" ? "Estate overview" : "Detailed estate map", "nce-map-estate-preview__section-label"));
  if (estate.shortDescription) body.append(text("p", estate.shortDescription, "nce-map-estate-preview__summary"));
  const topActions = text("div", "", "nce-map-drawer__actions nce-map-drawer__actions--top");
  topActions.append(estateInformationCta(frame, estate, scope));
  body.append(topActions);

  const facts = text("div", "", "nce-map-estate-preview__facts");
  const availability = text("div", "");
  availability.dataset.mapEstateAvailability = "";
  availability.append(text("strong", total === null ? "Checking" : total ? String(total) : "By enquiry"), text("span", total === 1 ? "property available" : total !== null && total > 1 ? "properties available" : "current availability"));
  const location = text("div", "");
  location.append(text("strong", estate.area.name), text("span", "North Coast location"));
  facts.append(availability, location);
  body.append(facts);

  if (scope === "master") {
    const propertySection = text("section", "", "nce-map-estate-preview__properties");
    propertySection.dataset.mapEstateProperties = "";
    const propertyHeading = text("div", "", "nce-map-estate-preview__properties-heading");
    propertyHeading.append(text("h3", "Properties"), text("span", total === null ? "Checking availability" : total ? `${total} available` : "Private enquiries"));
    propertySection.append(propertyHeading);
    if (total === null) {
      const loading = text("div", "", "nce-map-estate-preview__property-loading");
      loading.append(text("span", "Current property"), text("strong", "Loading availability…"));
      propertySection.append(loading);
    } else if (listings.length) {
      for (const listing of listings.slice(0, 2)) {
        const link = text("a", "", "nce-map-estate-preview__property");
        link.href = propertyUrl(listing.reference);
        const propertyMedia = text("span", "", "nce-map-estate-preview__property-media");
        if (listing.media[0]) {
          const propertyImage = document.createElement("img");
          propertyImage.src = listing.media[0].url;
          propertyImage.alt = listing.media[0].alt;
          propertyImage.loading = "lazy";
          propertyMedia.append(propertyImage);
        } else propertyMedia.append(text("span", "NCE"));
        const propertyCopy = text("span", "", "nce-map-estate-preview__property-copy");
        propertyCopy.append(text("small", `${listing.bedrooms ?? "—"} ${listing.bedrooms === 1 ? "bed" : "beds"} · ${listing.bathrooms ?? "—"} ${listing.bathrooms === 1 ? "bath" : "baths"}`), text("strong", listing.title), text("span", listing.priceVisible ? money(listing.price, listing.currency) : listing.priceQualifier || "Price on application"));
        link.append(propertyMedia, propertyCopy);
        propertySection.append(link);
      }
    } else {
      const empty = text("div", "", "nce-map-estate-preview__empty");
      empty.append(text("span", "By private enquiry"), text("strong", "Current & off-market homes"), text("p", "Speak to North Coast Estates about availability in this estate."));
      propertySection.append(empty);
    }
    body.append(propertySection);
  } else body.append(text("p", "Published estate plan · available homes are positioned using each listing's approved map precision", "nce-map-geometry-status"));

  body.append(text("p", "An independent guide by North Coast Estates. Not published by or on behalf of the estate.", "nce-map-estate-preview__independent"));
  const actions = text("div", "", "nce-map-drawer__actions");
  const map = text("a", scope === "master" ? "View detailed estate map" : "View estate property", "nce-text-link");
  map.href = estatePageUrl(frame, estate.slug, scope === "master" ? "map" : "property");
  actions.append(estateInformationCta(frame, estate, scope), map);
  body.append(actions);
  preview.append(media, body);
  fragment.append(preview);
  return fragment;
}

function estateDrawerOpeningContent(slug: string): DocumentFragment {
  const fragment = document.createDocumentFragment();
  const preview = text("article", "", "nce-map-estate-preview nce-map-estate-preview--opening");
  const media = text("div", "", "nce-map-estate-preview__media");
  const image = document.createElement("img");
  image.src = estateImage(slug);
  image.alt = "";
  image.fetchPriority = "high";
  media.append(image);
  const body = text("div", "", "nce-map-estate-preview__body nce-map-estate-preview__opening-body");
  body.append(text("p", "Estate overview", "nce-map-estate-preview__section-label"));
  body.append(text("h2", slug.split("-").map((part) => part.charAt(0).toUpperCase() + part.slice(1)).join(" "), "nce-map-estate-preview__opening-title"));
  body.append(text("p", "Preparing the current guide and available property…", "nce-map-estate-preview__opening-status"));
  const skeleton = text("div", "", "nce-map-estate-preview__opening-skeleton");
  skeleton.append(text("i", ""), text("i", ""), text("i", ""));
  body.append(skeleton, text("span", "Loading estate information", "screen-reader-text"));
  preview.append(media, body);
  fragment.append(preview);
  return fragment;
}

function embeddedEstateDrawerPayload(frame: HTMLElement): EstateDrawerPayload {
  const script = frame.querySelector<HTMLScriptElement>("[data-map-preview-data]");
  if (!script?.textContent) return { estates: [], listings: [] };
  try {
    const value = JSON.parse(script.textContent) as Partial<EstateDrawerPayload>;
    return { estates: Array.isArray(value.estates) ? value.estates : [], listings: Array.isArray(value.listings) ? value.listings : [] };
  } catch {
    return { estates: [], listings: [] };
  }
}

function card(listing: PublicListing): HTMLElement {
  const article = text("article", "", "nce-listing-card");
  const link = text("a", "");
  link.href = propertyUrl(listing.reference);
  const media = text("div", "", `nce-card-media${listing.media[0] ? "" : " nce-card-media--placeholder"}`);
  if (listing.media[0]) {
    const image = document.createElement("img");
    image.src = listing.media[0].url;
    image.alt = listing.media[0].alt;
    image.loading = "lazy";
    media.append(image);
  } else media.append(text("span", "NCE"));
  media.append(text("span", listing.status.replaceAll("_", " "), "nce-card-status"));
  const body = text("div", "", "nce-card-body");
  body.append(text("p", listing.estate.name, "nce-eyebrow"), text("h3", listing.title), text("p", listing.priceVisible ? money(listing.price, listing.currency) : listing.priceQualifier || "Price on application", "nce-card-price"));
  link.append(media, body);
  article.append(link);
  return article;
}

function renderEstatePlan(canvas: HTMLElement, plan: PublicEstatePlan, development: string, onPoint: (point: PublicEstatePlan["points"][number]) => void): void {
  canvas.replaceChildren();
  canvas.dataset.mapMode = "estate-plan";
  canvas.dataset.planZoomed = "false";
  canvas.parentElement?.querySelector("[data-plan-controls]")?.remove();
  const stage = text("div", "", "nce-estate-plan-stage");
  const image = document.createElement("img");
  image.src = plan.imageUrl; image.alt = `${plan.estateSlug.replaceAll("-", " ")} illustrated estate plan`; image.draggable = false;
  stage.append(image);
  const visiblePoints = development
    ? plan.points.filter((item) => item.kind === "property" && item.developmentSlug === development)
    : plan.points.filter((item) => item.kind !== "property");

  let baseWidth = 0;
  let baseHeight = 0;
  let zoom = 1;
  let initialDevelopmentFocusApplied = false;

  const controls = text("div", "", "nce-estate-plan-controls");
  controls.dataset.planControls = "true";
  const zoomOut = text("button", "−") as HTMLButtonElement;
  const fit = text("button", "Fit") as HTMLButtonElement;
  const zoomIn = text("button", "+") as HTMLButtonElement;
  zoomOut.type = fit.type = zoomIn.type = "button";
  zoomOut.setAttribute("aria-label", "Zoom estate plan out");
  fit.setAttribute("aria-label", "Fit the complete estate plan");
  zoomIn.setAttribute("aria-label", "Zoom estate plan in");
  controls.append(zoomOut, fit, zoomIn);
  canvas.parentElement?.append(controls);

  const applyZoom = (nextZoom: number): void => {
    if (!baseWidth || !baseHeight) return;
    const previousWidth = baseWidth * zoom;
    const previousHeight = baseHeight * zoom;
    const focusX = previousWidth > 0 ? (canvas.scrollLeft + canvas.clientWidth / 2) / previousWidth : .5;
    const focusY = previousHeight > 0 ? (canvas.scrollTop + canvas.clientHeight / 2) / previousHeight : .5;
    zoom = Math.max(1, Math.min(3, Math.round(nextZoom * 4) / 4));
    stage.style.width = `${Math.floor(baseWidth * zoom)}px`;
    stage.style.height = `${Math.floor(baseHeight * zoom)}px`;
    canvas.dataset.planZoomed = String(zoom > 1);
    canvas.dataset.planZoom = String(zoom);
    fit.textContent = zoom === 1 ? "Fit" : `${Math.round(zoom * 100)}%`;
    zoomOut.disabled = zoom === 1;
    zoomIn.disabled = zoom === 3;
    requestAnimationFrame(() => {
      if (zoom === 1) {
        canvas.scrollLeft = 0;
        canvas.scrollTop = 0;
        return;
      }
      canvas.scrollLeft = Math.max(0, focusX * baseWidth * zoom - canvas.clientWidth / 2);
      canvas.scrollTop = Math.max(0, focusY * baseHeight * zoom - canvas.clientHeight / 2);
    });
  };

  const fitPlanToCanvas = (): void => {
    if (!image.naturalWidth || !image.naturalHeight) return;
    const canvasStyle = window.getComputedStyle(canvas);
    const horizontalPadding = parseFloat(canvasStyle.paddingLeft) + parseFloat(canvasStyle.paddingRight);
    const verticalPadding = parseFloat(canvasStyle.paddingTop) + parseFloat(canvasStyle.paddingBottom);
    const availableWidth = Math.max(1, canvas.clientWidth - horizontalPadding);
    const availableHeight = Math.max(1, canvas.clientHeight - verticalPadding);
    const scale = Math.min(availableWidth / image.naturalWidth, availableHeight / image.naturalHeight);
    baseWidth = Math.floor(image.naturalWidth * scale);
    baseHeight = Math.floor(image.naturalHeight * scale);
    if (development && visiblePoints.length && !initialDevelopmentFocusApplied) {
      initialDevelopmentFocusApplied = true;
      const focusX = visiblePoints.reduce((sum, point) => sum + point.x, 0) / visiblePoints.length;
      const focusY = visiblePoints.reduce((sum, point) => sum + point.y, 0) / visiblePoints.length;
      applyZoom(1.75);
      requestAnimationFrame(() => {
        canvas.scrollLeft = Math.max(0, focusX * baseWidth * zoom - canvas.clientWidth / 2);
        canvas.scrollTop = Math.max(0, focusY * baseHeight * zoom - canvas.clientHeight / 2);
      });
    } else applyZoom(zoom);
  };

  image.addEventListener("load", fitPlanToCanvas, { once: true });
  if (image.complete) fitPlanToCanvas();
  new ResizeObserver(fitPlanToCanvas).observe(canvas);

  zoomOut.addEventListener("click", () => applyZoom(zoom - .25));
  zoomIn.addEventListener("click", () => applyZoom(zoom + .25));
  fit.addEventListener("click", () => applyZoom(1));
  canvas.addEventListener("wheel", (event) => {
    if (!event.ctrlKey) return;
    event.preventDefault();
    applyZoom(zoom + (event.deltaY < 0 ? .25 : -.25));
  }, { passive: false });

  let dragging = false;
  let dragX = 0;
  let dragY = 0;
  let scrollLeft = 0;
  let scrollTop = 0;
  canvas.addEventListener("pointerdown", (event) => {
    if (zoom === 1 || (event.target as Element).closest("button")) return;
    dragging = true;
    dragX = event.clientX;
    dragY = event.clientY;
    scrollLeft = canvas.scrollLeft;
    scrollTop = canvas.scrollTop;
    canvas.setPointerCapture(event.pointerId);
    canvas.dataset.planDragging = "true";
  });
  canvas.addEventListener("pointermove", (event) => {
    if (!dragging) return;
    canvas.scrollLeft = scrollLeft - (event.clientX - dragX);
    canvas.scrollTop = scrollTop - (event.clientY - dragY);
  });
  const stopDragging = (event: PointerEvent): void => {
    if (!dragging) return;
    dragging = false;
    if (canvas.hasPointerCapture(event.pointerId)) canvas.releasePointerCapture(event.pointerId);
    delete canvas.dataset.planDragging;
  };
  canvas.addEventListener("pointerup", stopDragging);
  canvas.addEventListener("pointercancel", stopDragging);
  for (const [index, point] of visiblePoints.entries()) {
    const marker = text("button", "", `nce-estate-plan-pin nce-estate-plan-pin--${point.kind}`);
    marker.type = "button"; marker.style.left = `${point.x * 100}%`; marker.style.top = `${point.y * 100}%`;
    marker.setAttribute("aria-label", point.label); marker.title = point.label;
    marker.append(text("i", point.kind === "property" && development ? String(index + 1).padStart(2, "0") : point.kind === "property" ? "" : point.kind.slice(0,1).toUpperCase()), text("span", point.label));
    marker.addEventListener("click", () => onPoint(point));
    stage.append(marker);
  }
  canvas.append(stage);
}

function filters(form: HTMLFormElement): Partial<MapState> {
  const data = new FormData(form);
  const estate = String(data.get("estate") || "");
  const kind = String(data.get("kind") || "");
  const bedrooms = data.get("bedrooms") ? Number(data.get("bedrooms")) : undefined;
  return { ...(estate ? { estate } : {}), ...(["all", "buy", "sale", "rental", "development", "vacant_land"].includes(kind) ? { kind: kind as NonNullable<MapState["kind"]> } : {}), ...(bedrooms !== undefined ? { bedrooms } : {}) };
}

export function mapQueryWithEstateCategory(query: string, category: EstateCategory | null): string {
  const params = new URLSearchParams(query);
  if (category) params.set("estateCategory", category);
  else params.delete("estateCategory");
  const value = params.toString();
  return value ? `?${value}` : "";
}

/** Filter existing markers/options only: no refetch, camera change or listing reassignment. */
export function applyEstateCategoryVisibility(
  markers: Iterable<HTMLElement>,
  picker: HTMLSelectElement | null,
  allOptions: readonly HTMLOptionElement[],
  category: EstateCategory | null,
  resolveCategory: (slug: string) => EstateCategory | null,
): boolean {
  const matches = (slug: string): boolean => category === null || resolveCategory(slug) === category;
  for (const marker of markers) {
    const slug = marker.dataset.estateSlug || "";
    const visible = matches(slug);
    marker.dataset.estateCategory = resolveCategory(slug) || "unclassified";
    marker.hidden = !visible;
    marker.setAttribute("aria-hidden", String(!visible));
    // Author display rules can override the browser's default [hidden] style.
    if (visible) marker.style.removeProperty("display");
    else marker.style.setProperty("display", "none", "important");
  }
  if (!picker) return false;
  const selected = picker.value;
  const excluded = Boolean(selected && !matches(selected));
  const visibleOptions = allOptions.filter((option) => !option.value || matches(option.value));
  // Detaching options also works in browsers that ignore hidden on <option>.
  if (picker.options.length !== visibleOptions.length || visibleOptions.some((option, index) => picker.options[index] !== option)) {
    picker.replaceChildren(...visibleOptions);
  }
  picker.value = excluded ? "" : selected;
  return excluded;
}

function initMaps(client: NceApiClient): void {
  document.querySelectorAll<HTMLElement>("[data-nce-map]").forEach((frame) => {
    const canvas = frame.querySelector<HTMLElement>(".nce-map-canvas");
    const fallback = frame.querySelector<HTMLElement>("[data-map-fallback]");
    const drawer = frame.querySelector<HTMLElement>("[data-map-drawer]");
    const drawerBody = frame.querySelector<HTMLElement>("[data-drawer-content]");
    const close = frame.querySelector<HTMLButtonElement>("[data-drawer-close]");
    const scrim = frame.querySelector<HTMLButtonElement>("[data-drawer-scrim]");
    const form = frame.querySelector<HTMLFormElement>("[data-map-filters]");
    const list = frame.querySelector<HTMLElement>("[data-map-list]");
    const count = frame.querySelector<HTMLElement>("[data-map-result-count]");
    if (!canvas || !config) return;

    const previewPayload = embeddedEstateDrawerPayload(frame);
    const estateCache = new Map<string, Promise<EstateDrawerEstate>>();
    const listingCache = new Map<string, Promise<EstateDrawerListingData>>();
    const resolvedListings = new Map<string, EstateDrawerListingData>();
    const loadEstate = (slug: string): Promise<EstateDrawerEstate> => {
      const cached = estateCache.get(slug);
      if (cached) return cached;
      const embeddedEstate = previewPayload.estates.find((estate) => estate.slug === slug);
      const request = embeddedEstate ? Promise.resolve(embeddedEstate) : client.estate(slug);
      estateCache.set(slug, request);
      request.catch(() => estateCache.delete(slug));
      return request;
    };
    const loadEstateListings = (slug: string): Promise<EstateDrawerListingData> => {
      const cached = listingCache.get(slug);
      if (cached) return cached;
      const request = client.listings({ estate: slug, perPage: 3 }).then((response) => ({ listings: response.data, total: response.pagination.total }));
      listingCache.set(slug, request);
      request.then((value) => resolvedListings.set(slug, value)).catch(() => listingCache.delete(slug));
      return request;
    };
    const prefetchEstateDrawer = (event: Event): void => {
      const target = event.target instanceof Element ? event.target.closest<HTMLElement>(".nce-estate-marker[data-estate-slug]") : null;
      const slug = target?.dataset.estateSlug;
      if (!slug) return;
      void loadEstate(slug);
      void loadEstateListings(slug).catch(() => undefined);
      const image = new Image();
      image.src = estateImage(slug);
    };
    frame.addEventListener("pointerover", prefetchEstateDrawer, { passive: true });
    frame.addEventListener("focusin", prefetchEstateDrawer);

    const scope = frame.dataset.mapScope === "estate" ? "estate" : "master";
    const params = frame.dataset.inheritQuery === "false" ? new URLSearchParams() : new URLSearchParams(window.location.search);
    if (frame.dataset.estate && !params.has("e")) params.set("e", frame.dataset.estate);
    if (frame.dataset.development && !params.has("d")) params.set("d", frame.dataset.development);
    const categorySelect = scope === "master" ? form?.querySelector<HTMLSelectElement>("[data-estate-category-filter]") : null;
    const estateSelect = form?.querySelector<HTMLSelectElement>('[name="estate"]') || null;
    const allEstateOptions = estateSelect ? Array.from(estateSelect.options) : [];
    const explicitCategories = new Map<string, EstateCategory>();
    for (const option of allEstateOptions) {
      const category = parseEstateCategory(option.dataset.estateCategory);
      if (category) explicitCategories.set(option.value, category);
    }
    for (const estate of previewPayload.estates) {
      const category = parseEstateCategory(estate.estateCategory ?? estate.attributes?.estateCategory);
      if (category) explicitCategories.set(estate.slug, category);
    }
    const categoryFor = (slug: string): EstateCategory | null => getEstateCategory(slug, explicitCategories.get(slug));
    let estateCategory = categorySelect ? parseEstateCategory(params.get("estateCategory") ?? categorySelect.value) : null;
    if (categorySelect) categorySelect.value = estateCategory || "";
    let selectedEstateSlug = params.get("e") || estateSelect?.value || "";
    if (categorySelect && selectedEstateSlug && estateCategory && categoryFor(selectedEstateSlug) !== estateCategory) {
      params.delete("e");
      params.delete("l");
      selectedEstateSlug = "";
    }
    if (categorySelect && estateSelect) estateSelect.value = selectedEstateSlug;
    let currentMapQuery = params.toString();
    const syncMapQuery = (query: string): void => {
      currentMapQuery = query;
      if (frame.dataset.syncUrl !== "false") {
        const categoryQuery = scope === "master" ? mapQueryWithEstateCategory(query, estateCategory) : query;
        window.history.replaceState(window.history.state, "", `${window.location.pathname}${categoryQuery}${window.location.hash}`);
      }
    };
    frame.dataset.view = scope === "master" ? "map" : params.get("view") || frame.dataset.defaultView || "map";
    const developmentContext = frame.dataset.mapContext === "development";
    let drawerRequest = 0;
    let drawerCloseTimer = 0;
    let controller: NceMapController | null = null;
    let mapResizeFrame = 0;
    const story = frame.closest<HTMLElement>(".nce-map-story");
    const homepageMapStory = story?.classList.contains("nce-map-story--map-only") ? story : null;
    const syncMapSize = (): void => {
      window.cancelAnimationFrame(mapResizeFrame);
      mapResizeFrame = window.requestAnimationFrame(() => controller?.map.resize());
    };
    const mapSizeObserver = new ResizeObserver(syncMapSize);
    mapSizeObserver.observe(frame);
    const setDrawerState = (open: boolean): void => {
      frame.toggleAttribute("data-drawer-open", open);
      story?.toggleAttribute("data-map-focus", open);
      syncMapSize();
    };
    const setDrawerOrigin = (slug: string): void => {
      if (!drawer || scope !== "master" || !window.matchMedia("(min-width: 761px)").matches) return;
      const marker = frame.querySelector<HTMLElement>(`.nce-estate-marker[data-estate-slug="${CSS.escape(slug)}"]`);
      const portrait = marker?.querySelector<HTMLElement>(".nce-estate-marker__portrait") || marker;
      if (!portrait) return;
      const frameRect = frame.getBoundingClientRect();
      const markerRect = portrait.getBoundingClientRect();
      const drawerStyle = window.getComputedStyle(drawer);
      const drawerLeft = Number.parseFloat(drawerStyle.left) || 0;
      const drawerTop = Number.parseFloat(drawerStyle.top) || 0;
      const originX = markerRect.left + markerRect.width / 2 - frameRect.left - drawerLeft;
      const originY = markerRect.top + markerRect.height / 2 - frameRect.top - drawerTop;
      drawer.style.setProperty("--nce-drawer-origin-x", `${originX}px`);
      drawer.style.setProperty("--nce-drawer-origin-y", `${originY}px`);
    };
    const positionDrawerClose = (): void => {
      if (!drawer || !close || !drawer.hasAttribute("data-open")) return;
      if (scope === "master" && window.matchMedia("(min-width: 761px)").matches) {
        close.style.removeProperty("top");
        return;
      }
      const drawerTop = drawer.getBoundingClientRect().top;
      const headers = Array.from(document.querySelectorAll<HTMLElement>(".nce-header,.nce-microsite-header"));
      const visibleHeaderBottom = headers.reduce((bottom, header) => {
        const rect = header.getBoundingClientRect();
        return rect.bottom > 0 && rect.top <= 1 ? Math.max(bottom, rect.bottom) : bottom;
      }, 0);
      close.style.top = `${Math.max(18, visibleHeaderBottom - drawerTop + 16)}px`;
    };
    const closeDrawer = (restoreFocus = true): void => {
      drawerRequest += 1;
      frame.removeAttribute("data-drawer-preparing");
      drawer?.removeAttribute("aria-busy");
      window.clearTimeout(drawerCloseTimer);
      if (drawer) drawer.inert = true;
      if (drawer?.hasAttribute("data-open") && scope === "master" && window.matchMedia("(min-width: 761px)").matches) {
        drawer.setAttribute("data-closing", "");
        frame.setAttribute("data-drawer-closing", "");
        drawer.removeAttribute("data-open");
        drawerCloseTimer = window.setTimeout(() => {
          drawer.removeAttribute("data-closing");
          drawer.setAttribute("aria-hidden", "true");
          frame.removeAttribute("data-drawer-closing");
          setDrawerState(false);
        }, 640);
      } else {
        drawer?.removeAttribute("data-open");
        drawer?.setAttribute("aria-hidden", "true");
        setDrawerState(false);
      }
      if (scope === "master") {
        selectedEstateSlug = "";
        controller?.clearEstateSelection();
        if (estateSelect) estateSelect.value = "";
      }
      if (restoreFocus) canvas.focus({ preventScroll: true });
    };
    window.addEventListener("scroll", positionDrawerClose, { passive: true });
    window.addEventListener("resize", positionDrawerClose, { passive: true });
    const showEstateDrawer = async (slug: string) => {
      if (!drawer || !drawerBody) return;
      const request = ++drawerRequest;
      const firstOpen = !drawer.hasAttribute("data-open");
      window.clearTimeout(drawerCloseTimer);
      drawer.removeAttribute("data-closing");
      frame.removeAttribute("data-drawer-closing");
      drawer.setAttribute("aria-busy", "true");
      if (firstOpen) {
        frame.setAttribute("data-drawer-preparing", "");
        setDrawerOrigin(slug);
        drawerBody.replaceChildren(estateDrawerOpeningContent(slug));
        drawer.setAttribute("aria-hidden", "false");
        drawer.inert = false;
        drawer.scrollTop = 0;
        setDrawerState(true);
        drawer.getBoundingClientRect();
        drawer.setAttribute("data-open", "");
        positionDrawerClose();
      }
      let estate: EstateDrawerEstate | null = null;
      try {
        estate = await loadEstate(slug);
        if (request !== drawerRequest) return;
        drawer.style.setProperty("--nce-drawer-accent", estate.accent.primary);
        const listingData = resolvedListings.get(slug);
        drawerBody.replaceChildren(estateDrawerContent(frame, estate, scope, listingData?.listings || [], listingData?.total ?? null));
      } catch (error) {
        if (request !== drawerRequest) return;
        console.error("Unable to prepare estate drawer", error);
        drawerBody.replaceChildren(text("p", "This estate information could not be loaded. Please try again."));
      }
      if (request !== drawerRequest) return;
      frame.removeAttribute("data-drawer-preparing");
      setDrawerOrigin(slug);
      drawer.setAttribute("aria-hidden", "false");
      drawer.inert = false;
      drawer.scrollTop = 0;
      setDrawerState(true);
      drawer.removeAttribute("aria-busy");
      positionDrawerClose();
      if (scope === "master" && estate && !resolvedListings.has(slug)) {
        void loadEstateListings(slug).then((listingData) => {
          if (request !== drawerRequest || !drawer.hasAttribute("data-open")) return;
          const updated = estateDrawerContent(frame, estate!, scope, listingData.listings, listingData.total);
          const availability = updated.querySelector<HTMLElement>("[data-map-estate-availability]");
          const properties = updated.querySelector<HTMLElement>("[data-map-estate-properties]");
          if (availability) drawerBody.querySelector<HTMLElement>("[data-map-estate-availability]")?.replaceWith(availability);
          if (properties) drawerBody.querySelector<HTMLElement>("[data-map-estate-properties]")?.replaceWith(properties);
        }).catch((error) => console.warn("Unable to preload estate availability", error));
      }
    };

    if (scope === "estate" && frame.dataset.estate) {
      const showListing = async (reference: string) => {
        if (!drawer || !drawerBody) return;
        const request = ++drawerRequest;
        drawer.removeAttribute("data-open"); drawer.setAttribute("aria-busy", "true");
        frame.removeAttribute("data-drawer-open");
        try {
          const listing = await client.listing(reference);
          if (request !== drawerRequest) return;
          drawer.style.setProperty("--nce-drawer-accent", developmentContext ? "#D8C6A3" : "#10C7C7");
          drawerBody.replaceChildren(developmentContext ? developmentListingDrawerContent(frame, listing) : listingDrawerContent(listing));
        }
        catch (error) { console.error("Unable to load estate-plan property", error); drawerBody.replaceChildren(text("p", "This property could not be loaded. Please try again.")); }
        if (request !== drawerRequest) return;
        drawer.removeAttribute("aria-busy"); drawer.setAttribute("aria-hidden", "false"); drawer.inert = false; drawer.setAttribute("data-open", ""); frame.setAttribute("data-drawer-open", ""); positionDrawerClose(); close?.focus();
      };
      const showPoint = (point: PublicEstatePlan["points"][number]) => {
        if (point.kind === "property" && point.listingReference) { void showListing(point.listingReference); return; }
        if (!drawer || !drawerBody) return;
        drawer.style.setProperty("--nce-drawer-accent", point.kind === "poi" ? "#10C7C7" : "#062D6B");
        drawerBody.replaceChildren(poiDrawerContent(point, frame.dataset.estate || "")); drawer.removeAttribute("aria-busy"); drawer.setAttribute("aria-hidden", "false"); drawer.inert = false; drawer.setAttribute("data-open", ""); frame.setAttribute("data-drawer-open", ""); positionDrawerClose(); close?.focus();
      };
      void client.estatePlan(frame.dataset.estate).then((plan) => {
        renderEstatePlan(canvas, plan, frame.dataset.development || "", showPoint);
        frame.dataset.ready = "true"; canvas.dataset.planState = "ready";
        if (fallback) fallback.hidden = true;
        const status = frame.querySelector<HTMLElement>("[data-map-overlay-status]"); if (status) status.textContent = developmentContext
          ? `${plan.points.filter((point) => point.kind === "property" && point.developmentSlug === frame.dataset.development).length} new-build homes · select a home for renders and details`
          : `${plan.points.filter((point) => point.kind !== "property").length} estate places and new-home collections · select a marker for details`;
        const empty = frame.querySelector<HTMLElement>("[data-map-plan-empty]"); if (empty) empty.hidden = true;
      }).catch((error) => {
        console.warn("No published illustrated estate plan", error); canvas.dataset.planState = "missing";
        const empty = frame.querySelector<HTMLElement>("[data-map-plan-empty]"); if (empty) empty.hidden = false;
      });
      close?.addEventListener("click", () => closeDrawer());
      scrim?.addEventListener("click", () => closeDrawer());
      canvas.addEventListener("click", (event) => {
        if (!drawer?.hasAttribute("data-open")) return;
        if ((event.target as Element).closest(".nce-estate-plan-pin")) return;
        closeDrawer();
      });
      document.addEventListener("keydown", (event) => { if (event.key === "Escape" && drawer?.hasAttribute("data-open")) closeDrawer(); });
      return;
    }
    canvas.addEventListener("nce:map-overlays", (event) => {
      const status = frame.querySelector<HTMLElement>("[data-map-overlay-status]");
      const empty = frame.querySelector<HTMLElement>("[data-map-plan-empty]");
      const detail = (event as CustomEvent<{ count?: number }>).detail;
      const hasPlan = Boolean(detail?.count);
      canvas.dataset.planState = hasPlan ? "ready" : "missing";
      if (empty) empty.hidden = hasPlan;
      if (status) status.textContent = hasPlan ? "Interactive estate plan loaded · select a property pin for details" : "Verified estate plan being prepared";
    });

    try {
      const activeController = new NceMapController({
        container: canvas,
        api: client,
        styleUrl: config.mapStyle,
        estateMarkerImages,
        scope,
        initialSearch: params.toString(),
        onStateChange: (_state, query) => { syncMapQuery(query); },
        onEstateSelect: async (slug) => {
          if (categorySelect && estateCategory && categoryFor(slug) !== estateCategory) return;
          selectedEstateSlug = slug;
          if (categorySelect && estateSelect) estateSelect.value = slug;
          await showEstateDrawer(slug);
        },
        ...(scope === "master" ? { onMapBackgroundClick: closeDrawer } : {}),
        onListingSelect: async (reference) => {
          if (!drawer || !drawerBody) return;
          const request = ++drawerRequest;
          drawer.removeAttribute("data-open");
          drawer.setAttribute("aria-busy", "true");
          frame.removeAttribute("data-drawer-open");
          try {
            const listing = await client.listing(reference);
            if (request !== drawerRequest) return;
            drawerBody.replaceChildren(listingDrawerContent(listing));
          } catch (error) {
            if (request !== drawerRequest) return;
            console.error("Unable to prepare property drawer", error);
            drawerBody.replaceChildren(text("p", "This property could not be loaded. Please try again."));
          }
          if (request !== drawerRequest) return;
          drawer.removeAttribute("aria-busy");
          drawer.setAttribute("aria-hidden", "false");
          drawer.inert = false;
          drawer.setAttribute("data-open", "");
          frame.setAttribute("data-drawer-open", "");
          positionDrawerClose();
          close?.focus();
        },
      });
      controller = activeController;
      if (categorySelect) {
        const applyCategory = (): void => {
          const pickerExcluded = applyEstateCategoryVisibility(canvas.querySelectorAll<HTMLElement>(".nce-estate-marker[data-estate-slug]"), estateSelect, allEstateOptions, estateCategory, categoryFor);
          const selectionExcluded = Boolean(selectedEstateSlug && estateCategory && categoryFor(selectedEstateSlug) !== estateCategory);
          frame.dataset.estateCategory = estateCategory || "all";
          if (pickerExcluded || selectionExcluded) closeDrawer(false);
        };
        // Read already-loaded source metadata; never add a second map-estates request.
        activeController.map.on("sourcedata", (event) => {
          if (event.sourceId !== "nce-estates") return;
          const data = (activeController.map.getSource("nce-estates") as GeoJSONSource | undefined)?.serialize().data;
          if (!data || typeof data === "string" || data.type !== "FeatureCollection") return;
          for (const feature of data.features) {
            const properties = feature.properties;
            const category = parseEstateCategory(properties?.estateCategory);
            if (typeof properties?.slug === "string" && category) explicitCategories.set(properties.slug, category);
          }
          applyCategory();
        });
        const markerObserver = new MutationObserver(applyCategory);
        markerObserver.observe(canvas, { childList: true, subtree: true });
        activeController.map.once("remove", () => markerObserver.disconnect());
        categorySelect.addEventListener("change", () => {
          estateCategory = parseEstateCategory(categorySelect.value);
          categorySelect.value = estateCategory || "";
          applyCategory();
          syncMapQuery(currentMapQuery);
        });
        applyCategory();
      }
      if (homepageMapStory) {
        activeController.map.scrollZoom.disable();
        frame.dataset.scrollZoom = "click-only";
      }

      activeController.map.once("load", () => { if (fallback) fallback.hidden = true; frame.dataset.ready = "true"; });
      activeController.map.on("error", (event) => {
        frame.dataset.mapError = event.error?.message || "Map resource error";
        if (fallback) fallback.hidden = false;
      });

      const updateList = async () => {
        if (!form || !list) return;
        const values = filters(form);
        const result = await client.listings({ ...(values.estate ? { estate: values.estate } : {}), ...(values.kind ? { kind: values.kind } : {}), ...(values.bedrooms !== undefined ? { bedrooms: values.bedrooms } : {}), perPage: 24 });
        const grid = text("div", "", "nce-card-grid");
        result.data.forEach((listing) => grid.append(card(listing)));
        const old = list.querySelector(".nce-card-grid,.nce-empty-state");
        if (result.data.length) old?.replaceWith(grid); else old?.replaceWith(text("div", "No properties match these map filters yet.", "nce-empty-state"));
        if (count) count.textContent = String(result.pagination.total);
      };

      form?.addEventListener("change", (event) => {
        if (event.target === categorySelect) return;
        const values = filters(form);
        selectedEstateSlug = values.estate || "";
        void activeController.setFilters(values);
        if (values.estate) {
          if (scope === "master") void showEstateDrawer(values.estate);
        }
        void updateList();
      });
      frame.querySelectorAll<HTMLButtonElement>("button[data-map-mode]").forEach((button) => button.addEventListener("click", () => {
        const mode = button.dataset.mapMode as MapState["mode"];
        frame.dataset.view = mode;
        frame.querySelectorAll<HTMLButtonElement>("button[data-map-mode]").forEach((item) => item.setAttribute("aria-pressed", String(item === button)));
        void activeController.setFilters({ mode });
        setTimeout(() => activeController.map.resize(), 50);
      }));
      close?.addEventListener("click", () => closeDrawer());
      scrim?.addEventListener("click", () => closeDrawer());
      document.addEventListener("keydown", (event) => { if (event.key === "Escape" && drawer?.hasAttribute("data-open")) close?.click(); });
    } catch {
      if (fallback) fallback.hidden = false;
      frame.dataset.error = "true";
    }
  });
}

function initInventoryMaps(client: NceApiClient): void {
  document.querySelectorAll<HTMLElement>("[data-nce-inventory-map]").forEach((frame) => {
    const canvas = frame.querySelector<HTMLElement>("[data-inventory-map-canvas]");
    const count = frame.querySelector<HTMLElement>("[data-inventory-map-count]");
    const estate = frame.dataset.estate || "";
    if (!canvas || !estate || !config) return;
    const centre: [number, number] = [Number(frame.dataset.longitude || 31.22), Number(frame.dataset.latitude || -29.5)];
    const map = new maplibregl.Map({ container: canvas, style: config.mapStyle, center: centre, zoom: 13.3, attributionControl: false });
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), "top-right");
    map.addControl(new maplibregl.AttributionControl({ compact: true }));
    map.once("load", () => void client.mapListings({ estate }).then((collection) => {
      const features = collection.features.flatMap((feature) => {
        const geometry = feature.geometry as { type?: string; coordinates?: unknown } | null;
        if (geometry?.type !== "Point" || !Array.isArray(geometry.coordinates) || geometry.coordinates.length < 2) return [];
        const coordinates: [number, number] = [Number(geometry.coordinates[0]), Number(geometry.coordinates[1])];
        return [{ ...feature, geometry: { type: "Point" as const, coordinates } }];
      });
      if (count) count.textContent = features.length ? `${features.length} location-approved ${features.length === 1 ? "home" : "homes"}` : "No public property locations";
      map.addSource("nce-inventory", { type: "geojson", data: { ...collection, features } as never, cluster: true, clusterRadius: 42, clusterMaxZoom: 16 });
      map.addLayer({ id: "nce-inventory-clusters", type: "circle", source: "nce-inventory", filter: ["has", "point_count"], paint: { "circle-color": "#062D6B", "circle-radius": ["step", ["get", "point_count"], 17, 5, 21, 12, 25], "circle-stroke-color": "#10C7C7", "circle-stroke-width": 2 } });
      map.addLayer({ id: "nce-inventory-cluster-count", type: "symbol", source: "nce-inventory", filter: ["has", "point_count"], layout: { "text-field": ["get", "point_count_abbreviated"], "text-size": 11 }, paint: { "text-color": "#F7F6F2" } });
      map.addLayer({ id: "nce-inventory-homes", type: "circle", source: "nce-inventory", filter: ["!", ["has", "point_count"]], paint: { "circle-color": ["match", ["get", "publicPrecision"], "exact", "#062D6B", "#10C7C7"], "circle-radius": 8, "circle-stroke-color": "#F7F6F2", "circle-stroke-width": 3 } });
      map.on("click", "nce-inventory-clusters", (event) => {
        const feature = event.features?.[0]; const clusterId = Number(feature?.properties?.cluster_id);
        if (!feature || !Number.isFinite(clusterId) || feature.geometry.type !== "Point") return;
        const center = feature.geometry.coordinates as [number, number];
        (map.getSource("nce-inventory") as GeoJSONSource).getClusterExpansionZoom(clusterId).then((zoom) => map.easeTo({ center, zoom })).catch(() => undefined);
      });
      map.on("click", "nce-inventory-homes", (event) => {
        const reference = event.features?.[0]?.properties?.reference as string | undefined;
        if (reference) window.location.assign(propertyUrl(reference));
      });
      for (const layer of ["nce-inventory-clusters", "nce-inventory-homes"]) {
        map.on("mouseenter", layer, () => { map.getCanvas().style.cursor = "pointer"; });
        map.on("mouseleave", layer, () => { map.getCanvas().style.cursor = ""; });
      }
      const coordinates = features.map((feature) => feature.geometry.coordinates);
      if (coordinates.length === 1 && coordinates[0]) map.easeTo({ center: coordinates[0], zoom: 15 });
      if (coordinates.length > 1) {
        const first = coordinates[0];
        if (!first) return;
        const bounds = coordinates.slice(1).reduce((value, coordinate) => value.extend(coordinate), new maplibregl.LngLatBounds(first, first));
        map.fitBounds(bounds, { padding: 70, maxZoom: 15, duration: 0 });
      }
      frame.dataset.ready = "true";
    }).catch((error) => { console.warn("Unable to load estate property locations", error); frame.dataset.error = "true"; }));
  });
}

function initPropertyLocationMaps(): void {
  document.querySelectorAll<HTMLElement>("[data-nce-property-location]").forEach((frame) => {
    const canvas = frame.querySelector<HTMLElement>("[data-property-location-canvas]");
    if (!canvas || !config) return;
    const precision = frame.dataset.precision || "estate_centroid";
    const propertyPosition: [number, number] | null = frame.dataset.longitude && frame.dataset.latitude ? [Number(frame.dataset.longitude), Number(frame.dataset.latitude)] : null;
    const estatePosition: [number, number] | null = frame.dataset.estateLongitude && frame.dataset.estateLatitude ? [Number(frame.dataset.estateLongitude), Number(frame.dataset.estateLatitude)] : null;
    const centre: [number, number] = propertyPosition || estatePosition || [31.22, -29.5];
    const map = new maplibregl.Map({ container: canvas, style: config.mapStyle, center: centre, zoom: precision === "exact" ? 15.5 : precision === "approximate" ? 14.5 : 13, attributionControl: false });
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), "top-right");
    map.addControl(new maplibregl.AttributionControl({ compact: true }));
    map.once("load", () => {
      if (propertyPosition && precision === "approximate") {
        map.addSource("nce-property-location", { type: "geojson", data: { type: "Feature", properties: {}, geometry: { type: "Point", coordinates: propertyPosition } } });
        map.addLayer({ id: "nce-property-area", type: "circle", source: "nce-property-location", paint: { "circle-radius": 38, "circle-color": "rgba(16,199,199,.18)", "circle-stroke-color": "#10C7C7", "circle-stroke-width": 2 } });
      }
      const markerPosition = precision === "hidden" ? null : (propertyPosition || estatePosition);
      if (markerPosition) {
        const marker = text("span", "", `nce-property-location-pin nce-property-location-pin--${precision}`);
        marker.setAttribute("aria-hidden", "true");
        new maplibregl.Marker({ element: marker, anchor: "center" }).setLngLat(markerPosition).addTo(map);
      }
      frame.dataset.ready = "true";
    });
  });
}

const client = api();
if (client) {
  const init = () => { initMaps(client); initInventoryMaps(client); initPropertyLocationMaps(); };
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init, { once: true });
  else init();
}
